import { CustomerModel } from '../customer.model';
import { chargeDayCode, chargeDayDescription } from '../../../shared/utils/autopay-charge-day';

/**
 * Row-level markers shown in the Customers grid's trailing "Badges" cell.
 *
 * This registry is the single source of truth for the badge set: the grid cell
 * renders from it, the CSV/PDF export reads its labels, and the column filter is
 * populated from and matched against it. Adding a badge is one entry here plus a
 * `.row-badge--<x>` colour pair in customers.component.ts — no new column, and
 * nothing to keep in step by hand.
 */
export interface CustomerBadge {
  /** Stable key, used for @for tracking. */
  code: string;
  /**
   * The badge's stable name — the value shown in the filter dropdown and the
   * one the column filter matches on. One per badge, so every autopay customer
   * files under `AUTOPAY` whatever day they are charged.
   */
  label: string;
  /**
   * Text on the chip and in the export, resolved for this customer. Equal to
   * `label` unless the badge says something per customer (`AUTOPAY-MON`).
   */
  text: string;
  /** Modifier class supplying the colour pair, resolved for this customer. */
  className: string;
  tooltip: string;
}

/**
 * A badge as declared in the registry.
 *
 * `className` and `tooltip` may be functions of the customer, because the payment-method badges
 * change colour to mark the **default** account — the same badge is green on one row and grey on
 * the next. `customerBadges()` resolves them, so everything downstream still sees plain strings.
 */
interface CustomerBadgeDefinition {
  code: string;
  label: string;
  /** Chip text when it differs per customer; defaults to `label`. */
  text?: (customer: CustomerModel) => string;
  className: string | ((customer: CustomerModel) => string);
  tooltip: string | ((customer: CustomerModel) => string);
  /** Whether this badge applies to a given customer. */
  applies: (customer: CustomerModel) => boolean;
}

/** True when the customer holds at least one token on this rail. */
const hasCard = (c: CustomerModel) => !!c.paymentTypes?.includes('CARD');
const hasAch = (c: CustomerModel) => !!c.paymentTypes?.includes('ACH');

/**
 * Green marks the account the customer pays with by default — which is not decoration: autopay
 * charges the default token and `AutoPayService` skips a customer that has none. A rail that is on
 * file but not the default stays neutral.
 */
const railClass = (rail: 'CARD' | 'ACH') => (c: CustomerModel) =>
  c.defaultPaymentType === rail ? 'row-badge--default' : 'row-badge--onfile';

const railTooltip = (rail: 'CARD' | 'ACH', noun: string) => (c: CustomerModel) =>
  c.defaultPaymentType === rail
    ? `${noun} on file, and the customer's default account — autopay and one-click payments use it`
    : `${noun} on file, but not the customer's default account`;

const CUSTOMER_BADGE_DEFINITIONS: CustomerBadgeDefinition[] = [
  {
    code: 'autopay',
    label: 'AUTOPAY',
    // Says WHEN as well as whether: AUTOPAY-MON (the Monday on or before each due
    // date), AUTOPAY-IMMEDIATE (from the invoice date), AUTOPAY-DUE (on the due
    // date — the default). The filter still files them all under AUTOPAY.
    text: (c) => `AUTOPAY-${chargeDayCode(c.customerSettings?.autopayDayOfWeek)}`,
    className: 'row-badge--autopay',
    tooltip: (c) =>
      `Enrolled in autopay — ${chargeDayDescription(c.customerSettings?.autopayDayOfWeek)}, to the default account`,
    applies: (c) => c.customerSettings?.enableAutopay === true,
  },
  {
    code: 'card',
    label: 'CC',
    className: railClass('CARD'),
    tooltip: railTooltip('CARD', 'Card'),
    applies: hasCard,
  },
  {
    code: 'ach',
    label: 'ACH',
    className: railClass('ACH'),
    tooltip: railTooltip('ACH', 'Bank account'),
    applies: hasAch,
  },
  {
    code: 'child',
    label: 'CHILD',
    className: 'row-badge--child',
    // Names the parent when the payload carries `parentCustomerName`, and falls back to the
    // plain wording when it does not — the customer list sends only `parentCustomerId`.
    tooltip: (c) =>
      c.parentCustomerName
        ? `Child account — billed through ${c.parentCustomerName}`
        : 'Child account — it belongs to a parent customer',
    applies: (c) => !!c.parentCustomerId,
  },
  {
    code: 'ship-to',
    label: 'SHIP-TO',
    className: 'row-badge--ship-to',
    // A SEPARATE badge from `child`, not a relabelling of it: having a parent is a
    // relationship, while this is the ERP saying the account holds no A/R at all.
    // A mock customer is a child and can still be charged; this one cannot, and
    // Collect Payment drops it entirely.
    tooltip: (c) =>
      c.parentCustomerName
        ? `Delivery location only — the ERP bills ${c.parentCustomerName}, so payment is taken there`
        : 'Delivery location only — the ERP bills a different account, so payment is taken there',
    applies: (c) => c.shipToOnly === true,
  },
  {
    code: 'test',
    label: 'TEST',
    className: 'row-badge--test',
    tooltip: 'Designated test customer',
    applies: (c) => c.isTestCustomer === true,
  },
  {
    code: 'inactive',
    label: 'INACTIVE',
    className: 'row-badge--inactive',
    tooltip: 'Retired in the source ERP — kept for its history, not a hard delete',
    applies: (c) => c.inactive === true,
  },
];

const resolve = <T>(value: T | ((customer: CustomerModel) => T), customer: CustomerModel): T =>
  typeof value === 'function' ? (value as (customer: CustomerModel) => T)(customer) : value;

/** The badges that apply to one customer, in registry order. */
export function customerBadges(customer: CustomerModel): CustomerBadge[] {
  return CUSTOMER_BADGE_DEFINITIONS.filter((badge) => badge.applies(customer)).map((badge) => ({
    code: badge.code,
    label: badge.label,
    text: badge.text ? badge.text(customer) : badge.label,
    className: resolve(badge.className, customer),
    tooltip: resolve(badge.tooltip, customer),
  }));
}

/** Filter dropdown options — every badge, whether or not any row has it. */
export const CUSTOMER_BADGE_LABELS: string[] = CUSTOMER_BADGE_DEFINITIONS.map((b) => b.label);

/** Space-joined chip texts; what the CSV/PDF export writes for the badge column. */
export function customerBadgeLabels(customer: CustomerModel): string {
  return customerBadges(customer)
    .map((b) => b.text)
    .join(' ');
}

/** True when the customer carries the badge with this label. */
export function customerHasBadge(customer: CustomerModel, label: string): boolean {
  return customerBadges(customer).some((b) => b.label === label);
}
